/**
 * Shared bootstrap utilities for app initialization.
 *
 * These utilities are used by both the main app and admin app entry points
 * to avoid code duplication.
 */

import type { App } from 'vue'
import type { Router } from 'vue-router'
import ApiService from '@/services/api.service'
import * as Sentry from '@sentry/vue'
import { watch } from 'vue'
import { useUserStore } from '@/store/userStore'
import { useLocaleStore } from '@/store/localStore'
import en from '@/i18n/yup/en'
import de from '@/i18n/yup/de'
import fr from '@/i18n/yup/fr'
import { setLocale } from 'yup'
import { OpenAPI } from '@/types/generated/core/OpenAPI'
import { OpenAPI as AdminOpenAPI } from '@/types/generated-admin/core/OpenAPI'
import { genericErrorToast } from '@/lib/toasts'
import type { OpenAPIConfig } from '@/types/generated/core/OpenAPI'

interface SetupApiV3Options {
  apiUrl: string
}

interface SetupApiV2Options {
  apiUrl: string
}

interface SetupSentryOptions {
  app: App
  router: Router
  dsn: string | undefined
  environment: string
  release?: string
  additionalIntegrations?: any[]
}

interface SetupTranslationsOptions {
  app: App
  i18n: any
}

/**
 * Configure an OpenAPI client instance with interceptors.
 *
 * Configures:
 * - Base URL and credentials
 * - CSRF token handling
 * - Access/session token support from URL params
 * - Global error toast for API errors
 */
const configureOpenApiClient = (openApi: OpenAPIConfig, apiUrl: string): void => {
  openApi.BASE = apiUrl.startsWith('/') ? window.location.origin : new URL(apiUrl).origin
  openApi.WITH_CREDENTIALS = true

  openApi.interceptors.request.use(async (request) => {
    const value = `; ${document.cookie}`
    const parts = value.split(`; csrftoken=`)
    const token = parts.pop()?.split(';').shift()
    const urlParams = new URLSearchParams(window.location.search)
    const accessToken = urlParams.get('access_token')
    const sessionToken = urlParams.get('session_token')

    // Get Office Add-in JWT from localStorage
    const officeAddinJwt = localStorage.getItem('office-addin-jwt')

    if (!request.headers) {
      request.headers = new Headers()
    } else if (!(request.headers instanceof Headers)) {
      const headers = new Headers()
      if (Array.isArray(request.headers)) {
        request.headers.forEach(([key, value]) => {
          headers.append(key, value)
        })
      } else {
        Object.entries(request.headers).forEach(([key, value]) => {
          headers.append(key, value)
        })
      }
      request.headers = headers
    }

    if (accessToken) {
      request.headers.set('X-Access-Token', accessToken)
    }
    if (sessionToken) {
      request.headers.set('X-Session-Token', sessionToken)
    }

    // Add Office Add-in JWT as Bearer token
    if (officeAddinJwt) {
      request.headers.set('Authorization', `Bearer ${officeAddinJwt}`)
    }

    request.headers.set('X-CSRFTOKEN', token ? token : '')
    return request
  })

  openApi.interceptors.response.use(async (response) => {
    if (
      response.status === 400 ||
      response.status === 401 ||
      response.status === 403 ||
      response.status === 404 ||
      response.status === 500
    ) {
      // Don't show error toast for 403 (auth redirect handling)
      if (response.status !== 403) {
        genericErrorToast()
      }
    }
    return response
  })
}

/**
 * Set up the OpenAPI v3 client (main API).
 *
 * :param options: Configuration options
 */
export const setupApiV3 = ({ apiUrl }: SetupApiV3Options): void => {
  configureOpenApiClient(OpenAPI, apiUrl)
}

/**
 * Set up the OpenAPI v3 admin client.
 *
 * Must be called in the admin app entry point so that services
 * imported from @/types/generated-admin use a properly configured client.
 *
 * :param options: Configuration options
 */
export const setupAdminApiV3 = ({ apiUrl }: SetupApiV3Options): void => {
  configureOpenApiClient(AdminOpenAPI, apiUrl)
}

/**
 * Set up the legacy API v2 service.
 *
 * :param options: Configuration options
 */
export const setupApiV2 = ({ apiUrl }: SetupApiV2Options): void => {
  ApiService.init(apiUrl)
  ApiService.setHeader()
}

/**
 * Set up Sentry error tracking and monitoring.
 *
 * Configures:
 * - Error tracking with sample rates
 * - Session replay
 * - User context when available
 * - Organization context as tags
 *
 * :param options: Configuration options including app, router, and Sentry settings
 */
export const setupSentry = ({
  app,
  router,
  dsn,
  environment,
  release,
  additionalIntegrations = []
}: SetupSentryOptions): void => {
  let tracesSampleRate = 0.0
  let replaysSessionSampleRate = 0.0
  let replaysOnErrorSampleRate = 0.0

  if (environment === 'production') {
    tracesSampleRate = 1.0
    replaysSessionSampleRate = 0.1
    replaysOnErrorSampleRate = 1.0
  }

  if (dsn !== undefined && dsn !== '') {
    Sentry.init({
      app,
      dsn,
      release,
      environment,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
        ...additionalIntegrations
      ],
      tracesSampleRate,
      replaysSessionSampleRate,
      replaysOnErrorSampleRate
    })

    try {
      const userStore = useUserStore()
      const { fetchUser } = userStore
      fetchUser().then((user) => {
        if (user) {
          Sentry.setUser({
            id: String(user.uid),
            email: user.email,
            username: `${user.first_name} ${user.last_name}`,
            first_name: user.first_name,
            last_name: user.last_name,
            role: user.role
          })

          if (user.org) {
            Sentry.setTag('org.id', String(user.org.oid))
            Sentry.setTag('org.uuid', user.org.uuid)
            Sentry.setTag('org.name', user.org.name)

            Sentry.setContext('organization', {
              id: String(user.org.oid),
              uuid: user.org.uuid,
              name: user.org.name,
              law_fields: user.org.law_fields,
              city: user.org.city,
              country: user.org.country,
              email: user.org.email
            })
          }
        }
      })
    } catch (error) {
      console.info('Failed to set up Sentry user details:', error)
    }
  }
}

/**
 * Lightweight Sentry init for environments without a Vue Router (e.g. Office add-in).
 *
 * Skips browser-tracing (requires router) and session replay.
 * User context can be set later via Sentry.setUser() once auth completes.
 */
export const setupSentryMinimal = ({
  app,
  dsn,
  environment,
  release
}: {
  app: App
  dsn: string | undefined
  environment: string
  release?: string
}): void => {
  if (!dsn) return

  Sentry.init({
    app,
    dsn,
    release,
    environment,
    tracesSampleRate: environment === 'production' ? 1.0 : 0.0
  })
}

/**
 * Set up translations and locale management.
 *
 * Configures:
 * - Initial locale loading
 * - Yup validation translations
 * - Locale change watching
 *
 * :param options: Configuration options including app and i18n instance
 */
export const setupTranslations = ({ app, i18n }: SetupTranslationsOptions): void => {
  const localeStore = useLocaleStore()

  const loadYupTranslations = (locale: string) => {
    switch (locale) {
      case 'de':
        setLocale(de)
        break
      case 'fr':
        setLocale(fr)
        break
      case 'en':
      default:
        setLocale(en)
        break
    }
  }

  // Set initial locale from localeStore
  i18n.global.locale.value = localeStore.getLocale
  loadYupTranslations(localeStore.getLocale)

  // Watch localeStore and sync with i18n
  watch(
    () => localeStore.getLocale,
    (newLocale) => {
      i18n.global.locale.value = newLocale
      loadYupTranslations(newLocale)
    }
  )

  app.use(i18n)
}
